import { ApiError, get, post, type Me } from '../api.js';
import {
  button, clear, drawer, el, empty, field, fmtMoney, input, mono, panel, raw, select,
  skeleton, table, tile, toast
} from '../ui.js';
import { STATES } from '../../../../packages/core/src/states.js';
import { isUnsourcedRate } from '../../../../packages/core/src/quoting.js';

/**
 * The quoter.
 *
 * WHERE EVERY NUMBER ON THIS SCREEN COMES FROM
 * A rate card the agency loaded from its own carrier. Nothing here estimates.
 * A product with no card shows "rates not loaded" and a link to that carrier's
 * own quoting tool — never a plausible premium, because an agent cannot tell an
 * invented number from a real one and the family finds out when the policy
 * issues at a different price.
 *
 * That is why the unpriced rows look deliberately unlike the priced ones. They
 * are different kinds of answer and the screen must never let them be read as
 * the same kind.
 */

const PLACES: Array<[string, string]> = [...STATES]
  .sort((a, b) => a.name.localeCompare(b.name, 'en'))
  .map((s) => [s.code, s.name]);

/* --------------------------------------------------------------- shapes -- */

interface Product {
  id: string; carrierId: string; name: string; productType: string;
  underwriting: string; minAge: number; maxAge: number;
  minFaceCents: number; maxFaceCents: number; termYears: number[];
  tobaccoAllowed: boolean; states: string[]; rateMode: 'table' | 'per_thousand';
  policyFeeCents: number; quoteUrl: string | null; notes: string | null;
  /** Null until somebody has checked these limits against the carrier's guide. */
  verifiedAt: string | null;
  sourceNote: string | null;
  archivedAt: string | null;
}
interface Carrier { id: string; name: string; archivedAt: string | null; agentPortalUrl?: string | null }
interface Priced {
  monthlyCents: number; annualCents: number; rateClass: string;
  termYears: number | null;
  /** What this premium buys. Several come back when no amount was asked for. */
  faceCents: number | null;
  /**
   * The card row this premium came off, exactly as the engine traced it.
   *
   * The API has always sent this and this screen threw it away — so a premium
   * typed from a carrier's own sheet and one written from memory rendered as
   * the same number in the same typeface. Dates arrive as strings here because
   * this is JSON, which `rateProvenance` is written to expect.
   */
  source?: { effectiveOn?: string | null; sourceNote?: string | null };
}
interface Section {
  carrier: Carrier;
  priced: Array<{
    product: Product; prices: Priced[]; seen: Seen[];
    drift: { cardCents: number; seenCents: number; at: string } | null;
  }>;
  unpriced: Array<{ product: Product; quoteUrl: string | null; seen: Seen[] }>;
  ineligible: Array<{ product: Product; reason: string }>;
  bestMonthlyCents: number | null;
  /** What this carrier sells, whatever the filters currently are. */
  writes: string[];
}
interface Seen {
  monthlyCents: number; age: number; faceCents: number;
  rateClass: string | null; source: 'card' | 'entered'; at: string;
}
interface Recommendation {
  carrierId: string; carrierName: string; productId: string; productName: string;
  monthlyCents: number; annualCents: number; rateClass: string; faceCents: number | null;
  why: string[]; caveats: string[]; notConsidered: number;
}
interface Run {
  applicant: { age: number; sex: string; tobacco: boolean; state: string; faceCents: number | null };
  sections: Section[];
  bestMonthlyCents: number | null;
  pricedCount: number;
  unpricedCount: number;
  recommendation: Recommendation | null;
}
interface Needs {
  totalNeedCents: number; offsetCents: number; gapCents: number;
  suggestedFaceCents: number; lines: Array<{ label: string; cents: number }>;
}

/* ---------------------------------------------------------------- money -- */

const toCents = (raw: string): number | null => {
  const s = raw.replace(/[$,\s]/g, '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};
const dollars = (c: number): string => String(Math.round(c) / 100);
/** Premiums are said out loud to the cent, so they are shown to the cent. */
const premium = (cents: number): string =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ----------------------------------------------------------------- view -- */

export function quoteView(me: Me, go: (p: string) => void): HTMLElement {
  const host = el('div');
  const results = el('div');

  host.append(el('div', { class: 'vhead' },
    el('span', { class: 'kicker', text: 'Quote' }),
    el('h1', { text: 'What can we write them?' }),
    el('p', {
      text: 'Every premium here comes off a rate card your agency loaded from the carrier. '
        + 'Anything without a card loaded says so and sends you to that carrier’s own quoter — '
        + 'it will never show you a number nobody will honour.',
    })));

  /* -- who is it for -------------------------------------------------- */
  const first = input({ placeholder: 'Rosa' });
  const last = input({ placeholder: 'Delgado' });
  const age = input({ type: 'number', min: '0', max: '120', placeholder: '58' });
  const dob = input({ type: 'date' });
  const sex = select([['female', 'Female'], ['male', 'Male']], 'female');
  const tobacco = select([['false', 'Non-smoker'], ['true', 'Smoker']], 'false');
  const state = select(PLACES, 'FL');
  const face = input({ placeholder: 'Every amount they print', inputmode: 'decimal' });
  const term = select([['', 'Not term']], '');

  /*
   * What kind of policy, and whose. Both start on "everything", because the
   * commonest question on a call is "what can we do for them at all" and only
   * then "just show me the term".
   */
  const kind = select([['', 'Every kind']], '');
  const carrierPick = el('div', { class: 'state-grid' });
  const chosenCarriers = new Set<string>();
  /* Kept so the scan can name who is being checked rather than say "loading". */
  let liveCarriers: Carrier[] = [];

  void (async () => {
    const [products, carriers, ref] = await Promise.all([
      get<Product[]>('/v1/products').catch(() => [] as Product[]),
      get<Carrier[]>('/v1/carriers').catch(() => [] as Carrier[]),
      get<{ productTypes: Array<{ code: string; label: string }> }>('/v1/reference'),
    ]);

    const terms = [...new Set(products.flatMap((p) => p.termYears))].sort((a, b) => a - b);
    for (const t of terms) term.append(el('option', { value: String(t) }, `${t}-year term`));

    // Only the kinds this agency actually sells — offering "annuity" to an
    // agency that writes none is a filter that can only ever return nothing.
    const sold = new Set(products.filter((p) => !p.archivedAt).map((p) => p.productType));
    for (const t of ref.productTypes.filter((t) => sold.has(t.code))) {
      kind.append(el('option', { value: t.code }, t.label));
    }

    clear(carrierPick);
    const live = carriers.filter((c) => !c.archivedAt);
    liveCarriers = live;
    if (!live.length) {
      carrierPick.append(el('span', { class: 'state-empty', text: 'No carriers set up yet.' }));
      return;
    }
    for (const c of live) {
      const cb = input({ type: 'checkbox' });
      cb.addEventListener('change', () => (cb.checked ? chosenCarriers.add(c.id) : chosenCarriers.delete(c.id)));
      carrierPick.append(el('label', { class: 'state-pick' }, cb, el('span', { text: c.name })));
    }
  })();

  const run = button('Quote it', 'pri', () => void quote());

  /*
   * Five boxes, then everything else folded away.
   *
   * An agent runs this with somebody on the phone. Age, sex, tobacco, state and
   * how much they want is the whole question nine times in ten — a screen that
   * opens with eleven fields makes them read all eleven before they can start
   * talking. The rest is still one click away and nothing has been removed.
   */
  const summary = el('span', { class: 'quo-more-note', text: 'Every amount · all carriers · every kind' });
  const refreshSummary = (): void => {
    const bits: string[] = [];
    bits.push(face.value ? `$${face.value}` : 'Every amount');
    bits.push(chosenCarriers.size ? `${chosenCarriers.size} carrier${chosenCarriers.size === 1 ? '' : 's'}` : 'all carriers');
    bits.push(kind.selectedIndex > 0 ? kind.options[kind.selectedIndex]!.text : 'every kind');
    if (term.value) bits.push(`${term.value}-year term`);
    if (dob.value) bits.push('from date of birth');
    summary.textContent = bits.join(' · ');
  };
  for (const control of [kind, term, dob, state]) control.addEventListener('change', refreshSummary);
  face.addEventListener('input', refreshSummary);
  carrierPick.addEventListener('change', refreshSummary);

  /*
   * Named rather than positioned. The phone layout gives this field a line of
   * its own, and `:last-child` would have handed that line to whatever field
   * somebody adds next.
   */
  const coverField = field('Cover wanted ($)', face,
    'Leave it empty and every carrier shows every amount its card prints.');
  coverField.classList.add('quo-cover');

  /*
   * Five boxes, and four of them are things you already know by the time
   * somebody has said hello.
   *
   * Cover wanted moved out of here because it is not needed to answer the
   * question an agent is actually asking at this point — "what can we do for
   * them?" — and a card prints every face amount anyway. State moved out
   * because every product loaded so far is filed wherever the producer is
   * licensed, so it was a required field that changed nothing.
   *
   * SEX STAYS, and it is not negotiable. Rate cards are filed separately for
   * men and women; there is no single premium to look up without it. Guessing
   * would return a real, precise, confident figure for the wrong person.
   */
  host.append(panel('Who is it for',
    el('div', { class: 'filters quo-who' },
      field('First name', first),
      field('Last name', last),
      field('Age', age, 'Or give a date of birth below.'),
      field('Sex', sex, 'Rate cards are filed by sex. Used for nothing else.'),
      field('Smoker', tobacco)),

    el('details', { class: 'quo-more' },
      el('summary', null,
        el('span', { text: 'Cover wanted, state, kind of policy, which carriers' }),
        summary),
      el('div', { class: 'filters', style: 'margin-top:12px' },
        coverField,
        field('State', state),
        field('Date of birth', dob),
        field('Kind of policy', kind),
        field('Term', term)),
      el('div', { class: 'field' },
        el('span', { text: 'Carriers to quote' }),
        el('small', { text: 'Leave them all unticked to quote every carrier, which is usually what you want.' }),
        carrierPick)),

    el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap;margin-top:14px' },
      run,
      button('Work out how much they need', 'sm ghost', () => openNeeds((suggested) => {
        face.value = dollars(suggested);
        toast(`Suggested ${fmtMoney(suggested)} of cover.`, 'ok');
      })))));

  host.append(results);

  async function quote(): Promise<void> {
    const faceCents = toCents(face.value);
    if (!age.value && !dob.value) { toast('Give an age, or a date of birth.', 'crit'); return; }

    clear(results);
    results.append(scanning(
      liveCarriers
        .filter((c) => chosenCarriers.size === 0 || chosenCarriers.has(c.id))
        .map((c) => c.name)));
    run.disabled = true;
    try {
      const body: Record<string, unknown> = {
        sex: sex.value, tobacco: tobacco.value === 'true',
        ...(state.value ? { state: state.value } : {}),
        ...(faceCents ? { faceCents } : {}),
        ...(age.value ? { age: Number(age.value) } : {}),
        ...(dob.value ? { dob: dob.value } : {}),
        ...(term.value ? { termYears: Number(term.value) } : {}),
        ...(kind.value ? { productTypes: [kind.value] } : {}),
        ...(chosenCarriers.size ? { carrierIds: [...chosenCarriers] } : {}),
      };
      const res = await post<Run>('/v1/quote', body);
      clear(results);
      results.append(renderRun(res, {
        applicantName: [first.value.trim(), last.value.trim()].filter(Boolean).join(' '),
        onSaved: () => toast('Quote saved.', 'ok'),
        go,
      }));
    } catch (e) {
      clear(results);
      results.append(el('div', { class: 'warnbox' },
        e instanceof ApiError ? e.message : 'Could not run that quote.'));
    } finally {
      run.disabled = false;
    }
  }

  void me;
  return host;
}

/* ---------------------------------------------------------------- scan -- */

/**
 * What the screen shows while the quote is running.
 *
 * A spinner says "wait". This says who is being checked, which is the thing
 * an agent actually wants to know when the person on the phone asks what is
 * taking so long — and it is honest: these are exactly the carriers the
 * request went out for.
 *
 * If the carrier list has not loaded yet there are no names to show, so it
 * falls back to the ordinary skeleton rather than inventing any.
 */
function scanning(carrierNames: string[]): HTMLElement {
  if (!carrierNames.length) return skeleton(5);

  const list = el('div', { class: 'qscan-list' });
  carrierNames.forEach((n, i) => {
    list.append(el('div', {
      class: 'qscan-one',
      // Staggered so they arrive in order, which reads as progress. Capped
      // so twenty carriers do not take two seconds to finish appearing.
      style: `animation-delay:${Math.min(i * 70, 700)}ms`,
    }, el('b', { text: n }), el('span', { text: 'checking rate cards' })));
  });

  return el('div', { class: 'qscan', 'aria-busy': 'true', 'aria-label': 'Quoting' },
    el('div', { class: 'qscan-head', text: `Quoting ${carrierNames.length} carrier${carrierNames.length === 1 ? '' : 's'}` }),
    list);
}

/* --------------------------------------------------------------- results -- */

function renderRun(
  res: Run,
  opts: { applicantName: string; onSaved: () => void; go: (p: string) => void },
): HTMLElement {
  const host = el('div', { class: 'grid' });

  /*
   * Three different nothings, and they need three different sentences.
   *
   * Getting to a quote is three steps — add the carrier, add what it sells,
   * load the rate card — and telling somebody the wrong one costs them a
   * search through a screen that was never wrong. This screen once said "No
   * carriers set up yet" to an agency with seven carriers on its Settings
   * page, because it counted products and reported carriers.
   */
  const nothingAnywhere = res.sections.every((s) => s.writes.length === 0);

  if (!res.sections.length) {
    host.append(panel(null, empty(
      'No carriers yet',
      'A quote starts with the carriers you are appointed with. Add them in '
      + 'Settings → Carriers, then add what each one sells.',
      button('Go to Settings → Carriers', 'pri', () => opts.go('/settings/carriers')))));
    return host;
  }

  if (nothingAnywhere) {
    const names = res.sections.map((s) => s.carrier.name);
    host.append(panel(null, empty(
      `${names.length} carrier${names.length === 1 ? '' : 's'}, no products yet`,
      `${names.slice(0, 4).join(', ')}${names.length > 4 ? ` and ${names.length - 4} more` : ''} `
      + `${names.length === 1 ? 'is' : 'are'} set up. What is missing is what each one sells — `
      + 'a product like "Living Promise" or "Immediate Solution", with its issue ages and face '
      + 'limits. Add one product and this screen starts working; load its rate card and it '
      + 'starts pricing.',
      button('Add products', 'pri', () => opts.go('/settings/products')))));
    return host;
  }

  const r = res.recommendation;
  if (r) {
    /*
     * Shown before the tables, with its reasons, because a recommendation an
     * agent cannot check is one they either follow blindly or ignore — and both
     * are worse than none at all.
     */
    const box = el('section', { class: 'panel rec' });
    box.append(
      el('div', { class: 'kicker', style: 'display:block;color:var(--good)', text: 'Lead with this' }),
      el('h2', {
        style: 'font-family:var(--f-display);font-size:19px;margin:8px 0 2px',
        text: `${r.carrierName} — ${r.productName}`,
      }),
      el('div', { class: 'rec-price', text: premium(r.monthlyCents) }),
      el('div', { class: 'rec-per' },
        r.faceCents === null ? '' : `for ${fmtMoney(r.faceCents)} of cover · `,
        'a month · ',
        el('b', { text: premium(r.annualCents) }),
        ` a year · ${r.rateClass}`),
      el('ul', { class: 'rec-why' }, ...r.why.map((w) => el('li', { text: w }))),
    );
    if (r.caveats.length) {
      box.append(el('div', { class: 'warnbox', style: 'margin-top:12px' },
        el('b', null, 'Before you say it out loud. '),
        ...r.caveats.map((cv) => el('div', { style: 'margin-top:4px', text: cv }))));
    }
    host.append(box);
  }

  const tiles = el('div', { class: 'tiles' });
  tiles.append(tile('Best premium',
    res.bestMonthlyCents === null ? '—' : premium(res.bestMonthlyCents),
    res.bestMonthlyCents === null ? 'no rate cards loaded yet' : 'a month, from a loaded card',
    'var(--good)'));
  tiles.append(tile('Priced', String(res.pricedCount), 'products with a card loaded'));
  tiles.append(tile('Not priced here', String(res.unpricedCount),
    'quote these in the carrier’s own tool', 'var(--warn)'));
  tiles.append(res.applicant.faceCents === null
    ? tile('Showing', 'Every amount', `age ${res.applicant.age}${res.applicant.tobacco ? ' · smoker' : ''}`)
    : tile('Cover', fmtMoney(res.applicant.faceCents),
      `age ${res.applicant.age}${res.applicant.state ? ` · ${res.applicant.state}` : ''}${res.applicant.tobacco ? ' · smoker' : ''}`));
  host.append(tiles);

  /*
   * The limits behind this answer, and whether anybody checked them.
   *
   * A premium is only half the answer. The other half is the set of products
   * that were allowed to compete for it — and that was decided by issue ages
   * and face bands somebody typed in. When those are unchecked the failure is
   * silent and works in both directions: a carrier that would have written this
   * person is quietly absent, or one that would have declined them is quietly
   * offered. Neither shows up as an error, so it has to show up here.
   *
   * Counted across every product on the screen, priced or not, because an
   * unchecked band is most dangerous exactly where it excluded somebody — and
   * an excluded product produces no premium to hang a warning off.
   */
  const seenProducts = new Map<string, Product>();
  for (const sec of res.sections) {
    for (const r of sec.priced) seenProducts.set(r.product.id, r.product);
    for (const u of sec.unpriced) seenProducts.set(u.product.id, u.product);
    for (const g of sec.ineligible) seenProducts.set(g.product.id, g.product);
  }
  const unchecked = [...seenProducts.values()].filter((pr) => !pr.verifiedAt);
  if (unchecked.length) {
    const carriers = [...new Set(unchecked.map((pr) =>
      res.sections.find((sec) => sec.carrier.id === pr.carrierId)?.carrier.name ?? ''))]
      .filter(Boolean).sort();
    host.append(el('div', { class: 'warnbox', style: 'margin:14px 0' },
      // "1 of these products has" reads oddly when there is only one product
      // on the screen at all, and "1 of these product" is simply wrong. Count
      // the thing, then agree the verb with the count.
      el('b', null, unchecked.length === 1
        ? 'One product here has issue limits nobody has checked. '
        : `${unchecked.length} products here have issue limits nobody has checked. `),
      el('span', null,
        'The ages and face amounts on '
        + (carriers.length > 3 ? `${carriers.length} carriers` : carriers.join(', '))
        + ' decide which of them could be quoted at all, and they have not been confirmed against '
        + 'a carrier document. Treat a missing carrier here as unproven rather than as a no. '),
      (() => {
        const b = el('button', { class: 'lnk', type: 'button', text: 'Check them in Settings → Products & rates' });
        b.addEventListener('click', () => opts.go('/settings/products'));
        return b;
      })()));
  }

  /*
   * And the other half of the same question, which used to go unasked.
   *
   * The warning above is about ISSUE LIMITS — the ages and face amounts that
   * decide whether a carrier appears at all. It says nothing about the premium,
   * and the premium is the thing the agent reads out loud.
   *
   * A product can have its limits confirmed against the carrier's guide and
   * still be quoting figures somebody typed from memory. On this screen those
   * two states looked identical, so a number nobody could trace and a number
   * off the carrier's own sheet were presented with equal confidence. That is
   * exactly the failure this quoter exists to prevent, arriving through the
   * data instead of through the code.
   *
   * Counted over premiums rather than products, because one product can price
   * several face amounts and it is the figures that get quoted.
   */
  const unsourcedPrices = res.sections.flatMap((sec) =>
    sec.priced.flatMap((r) => r.prices.filter((p) => p.source && isUnsourcedRate(p.source))));
  if (unsourcedPrices.length) {
    const carriers = [...new Set(res.sections
      .filter((sec) => sec.priced.some((r) =>
        r.prices.some((p) => p.source && isUnsourcedRate(p.source))))
      .map((sec) => sec.carrier.name))].sort();
    host.append(el('div', { class: 'warnbox', style: 'margin:14px 0' },
      el('b', null, unsourcedPrices.length === 1
        ? 'One premium here cannot be traced to a carrier document. '
        : `${unsourcedPrices.length} premiums here cannot be traced to a carrier document. `),
      el('span', null,
        'Nobody recorded which document or effective date '
        + (carriers.length > 3 ? `${carriers.length} carriers’ cards` : `${carriers.join(', ')}`)
        + ' was typed from. These are not estimates — they are figures somebody entered, and '
        + 'the system cannot tell whether they came off a rate sheet or out of a memory. '),
      el('b', null, 'Confirm them with the carrier before you quote them to anybody. '),
      (() => {
        const b = el('button', {
          class: 'lnk', type: 'button',
          text: 'Record where each card came from in Settings → Products & rates',
        });
        b.addEventListener('click', () => opts.go('/settings/products'));
        return b;
      })()));
  }

  /*
   * One panel per carrier — each carrier its own section, its own products.
   *
   * The rank and the bar are worked out from the priced carriers only. A
   * carrier with no card loaded has no place in a cheapest-to-dearest
   * ordering, so it gets no rank rather than a made-up last place.
   */
  const pricedBest = res.sections
    .map((s) => s.bestMonthlyCents)
    .filter((c): c is number => c !== null);
  const dearest = pricedBest.length ? Math.max(...pricedBest) : 0;
  let rank = 0;

  for (const s of res.sections) {
    const body = el('div');

    if (s.bestMonthlyCents !== null) {
      rank += 1;
      // Full bar for the cheapest, shorter the further behind it came. Never
      // below a sliver, so a carrier that is priced always looks priced.
      const w = dearest > 0
        ? Math.max(0.08, (res.bestMonthlyCents ?? s.bestMonthlyCents) / s.bestMonthlyCents)
        : 1;
      const over = s.bestMonthlyCents - (res.bestMonthlyCents ?? s.bestMonthlyCents);
      body.append(
        el('div', { class: 'qbar' }, el('i', { style: `--w:${w.toFixed(3)}` })),
        over > 0
          ? el('div', { class: 'qdelta', style: 'margin:-6px 0 10px', text: `${premium(over)} a month more than the cheapest` })
          : el('div', { class: 'qdelta', style: 'margin:-6px 0 10px;color:var(--good)', text: 'Cheapest of the carriers with a card loaded' }),
      );
    }

    if (s.priced.length) {
      /*
       * Rows, not a table.
       *
       * This was a five-column table and it was wrong on every phone made.
       * "Monthly" is the fourth column, so on a 390px screen the premium — the
       * one number the agent is about to say out loud to somebody — sat off the
       * right edge behind a sideways scroll. The page did not overflow, so no
       * width check anywhere caught it; the number simply could not be seen.
       *
       * A row puts the premium next to the product name at every width and
       * folds underneath it below 620px. Nothing to scroll, nothing to find.
       */
      const list = el('div', { class: 'qrows' });
      for (const row of s.priced) {
        for (const p of row.prices) {
          list.append(el('div', { class: 'qrow' },
            el('div', { class: 'qrow-what' },
              el('b', { text: p.faceCents === null ? row.product.name : fmtMoney(p.faceCents) }),
              el('div', { class: 'qrow-uw' },
                p.faceCents === null ? '' : `${row.product.name} · `,
                underwritingOf(row.product),
                p.rateClass ? ` · ${p.rateClass}` : '',
                p.termYears ? ` · ${p.termYears}-year term` : '')),
            el('div', { class: 'qrow-cost' },
              el('div', { class: 'qrow-mo mono', text: premium(p.monthlyCents) }),
              el('div', { class: 'qrow-yr mono', text: `${premium(p.annualCents)} a year` }),
              /*
               * The mark goes HERE, against the number, not only in a banner at
               * the top of the screen. An agent on a call reads one row out
               * loud; whatever is three hundred pixels above it is not in the
               * conversation. If this premium cannot be traced to a document,
               * the words have to be beside the figure being spoken.
               */
              p.source && isUnsourcedRate(p.source)
                ? el('div', {
                  class: 'chip crit',
                  style: 'margin-top:4px',
                  title: 'Nobody recorded which carrier document or effective date this '
                    + 'rate was typed from. Confirm it against the carrier before quoting it.',
                  text: 'rate not sourced',
                })
                : null),
            el('div', { class: 'qrow-do' },
              button('Save this quote', 'sm pri', async () => {
                try {
                  await post('/v1/quotes', {
                    carrierId: s.carrier.id, productId: row.product.id,
                    applicantName: opts.applicantName || undefined,
                    age: res.applicant.age, sex: res.applicant.sex,
                    tobacco: res.applicant.tobacco, state: res.applicant.state,
                    faceCents: p.faceCents ?? res.applicant.faceCents,
                    termYears: p.termYears ?? undefined,
                    rateClass: p.rateClass,
                    monthlyCents: p.monthlyCents,
                    // From a card, and recorded as such: this figure traces to a row.
                    source: 'card',
                  });
                  opts.onSaved();
                } catch (e) {
                  toast(e instanceof ApiError ? e.message : 'Could not save that quote.', 'crit');
                }
              }),
              row.drift
                ? el('span', {
                  class: 'chip crit',
                  title: `Their own tool said ${premium(row.drift.seenCents)} on ${row.drift.at.slice(0, 10)}`,
                  text: 'card may be stale',
                })
                : null)));
        }
      }
      body.append(list);
    }

    if (s.unpriced.length) {
      /*
       * Deliberately unlike the priced table above. These are not premiums,
       * they are products that could be written and have no card loaded, and
       * the screen must never let the two be read as the same kind of answer.
       */
      /*
       * Not `.okbox`. That box is green, and green on this screen means a
       * price came off a card — the exact reading this block has to prevent.
       * A carrier with no card is not an error either, so not `.warnbox`
       * (which is red) — it is simply not set up yet, and says so.
       */
      /*
       * The missing next action.
       *
       * This block told an agent what was wrong and offered only "record what
       * the carrier's tool gave you" — a workaround. The actual fix is to load
       * the card once, after which this carrier prices every case by itself
       * forever. With nine carriers unpriced that is nine identical notices
       * and no route to the one screen that ends them.
       */
      body.append(el('div', { class: 'note', style: 'margin-top:10px' },
        el('b', null, 'No rate card loaded for these. '),
        `Load ${s.carrier.name}’s card once and it prices every case from then on. `
        + 'Until then, price them in the carrier’s own tool and record what it gave you.',
        el('div', { style: 'margin-top:9px' },
          // The carrier's name is the heading directly above this, so the
          // button does not repeat it. It also does not fit on a 280px phone
          // when it does — `.btn` is `white-space: nowrap`.
          button('Load the rate card', 'sm pri',
            () => opts.go('/settings/products')))));
      for (const u of s.unpriced) {
        body.append(el('div', {
          style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:9px 0;'
            + 'border-bottom:1px solid var(--rim)',
        },
        el('b', { style: 'flex:1 1 auto', text: u.product.name }),
        u.quoteUrl
          ? el('a', {
            class: 'btn sm', href: u.quoteUrl, target: '_blank', rel: 'noopener noreferrer',
          }, `Quote at ${s.carrier.name}`)
          : el('span', { style: 'font-size:12px;color:var(--ink-4)', text: 'No quoting link on file' }),
        button('Record what it gave', 'sm', () => openRecord({
          carrier: s.carrier, product: u.product, applicant: res.applicant,
          applicantName: opts.applicantName, onSaved: opts.onSaved,
        }))));
        if (u.seen.length) {
          /*
           * History, and labelled as history. Each one carries the age and face
           * amount it was actually for, precisely so nobody reads it as today's
           * answer for today's applicant and repeats it down the phone.
           */
          body.append(el('div', { class: 'seen' },
            el('b', { text: 'What they came back with before: ' }),
            ...u.seen.map((sb) => el('span', { class: 'seen-one' },
              `${premium(sb.monthlyCents)} for a ${sb.age}-year-old at ${fmtMoney(sb.faceCents)}`
              + `${sb.rateClass ? `, ${sb.rateClass}` : ''} · ${sb.at.slice(0, 10)}`))));
        }
      }
    }

    if (s.priced.length + s.unpriced.length + s.ineligible.length === 0) {
      if (s.writes.length === 0) {
        // Nothing set up at all: a task, not an answer.
        body.append(el('div', { class: 'note' },
          el('b', null, 'Nothing set up for this carrier yet. '),
          'Add what they sell — a product, with its issue ages and face limits — and this '
          + 'carrier joins the comparison.'));
        body.append(el('div', { style: 'margin-top:10px' },
          button('Add a product', 'sm', () => opts.go('/settings/products'))));
      } else {
        /*
         * A finished, correct answer: this carrier is set up, it just does not
         * sell what was asked for. Saying "nothing set up" here would send an
         * agent to create a product the carrier does not write.
         */
        body.append(el('div', { class: 'note' },
          el('b', null, 'Not something this carrier writes. '),
          `${s.carrier.name} sells ${kindList(s.writes)} here.`));
      }
    }

    if (s.ineligible.length) {
      body.append(el('details', { style: 'margin-top:12px' },
        el('summary', {
          style: 'font-size:12.5px;color:var(--ink-3);cursor:pointer',
          text: `${s.ineligible.length} product${s.ineligible.length === 1 ? '' : 's'} cannot be written for this person`,
        }),
        ...s.ineligible.map((i) => el('div', {
          style: 'display:flex;gap:10px;justify-content:space-between;padding:7px 0;font-size:12.5px',
        },
        el('span', { text: i.product.name }),
        el('span', { style: 'color:var(--ink-4)', text: i.reason })))));
    }

    const sec = el('section', {
      class: `panel qsec${s.bestMonthlyCents !== null && rank === 1 ? ' qsec-1' : ''}`,
    });
    sec.append(
      el('h2', null,
        s.bestMonthlyCents === null
          ? el('span', { class: 'qrank', text: '—', title: 'No rate card loaded, so it has no place in the ordering' })
          : el('span', { class: 'qrank', text: String(rank) }),
        el('span', { text: s.carrier.name }),
        s.bestMonthlyCents === null
          ? null
          : el('span', { class: 'qprice', text: `${premium(s.bestMonthlyCents)}/mo` })),
      body,
    );
    host.append(sec);
  }

  return host;
}

/** "whole life and term life", from the codes the API returns. */
function kindList(codes: string[]): string {
  const label: Record<string, string> = {
    term: 'term life', whole_life: 'whole life', universal_life: 'universal life',
    indexed_universal_life: 'indexed universal life', guaranteed_universal_life: 'guaranteed universal life',
    variable_universal_life: 'variable universal life', final_expense: 'final expense',
    accidental_death: 'accidental death', annuity: 'annuities',
    mortgage_protection: 'mortgage protection', critical_illness: 'critical illness',
    disability: 'disability', long_term_care: 'long-term care', other: 'other cover',
  };
  const names = codes.map((c) => label[c] ?? c);
  if (names.length <= 1) return names[0] ?? 'nothing';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const underwritingOf = (p: Product): string => {
  const label: Record<string, string> = {
    full: 'Fully underwritten', accelerated: 'No exam if they qualify',
    simplified: 'Health questions, no exam', guaranteed: 'No health questions',
  };
  return label[p.underwriting] ?? p.underwriting;
};

/* ------------------------------------------------- recording what came back -- */

function openRecord(o: {
  carrier: Carrier; product: Product;
  applicant: Run['applicant']; applicantName: string; onSaved: () => void;
}): void {
  const body = el('div');
  const close = drawer(`What ${o.carrier.name} quoted`, body);

  const monthly = input({ placeholder: '58.42', inputmode: 'decimal' });
  const rateClass = input({ placeholder: 'Standard, preferred, graded…' });
  const notes = el('textarea', { class: 'inp', rows: '2', placeholder: 'Anything worth remembering' }) as HTMLTextAreaElement;

  const save = button('Save this quote', 'pri', async () => {
    const cents = toCents(monthly.value);
    if (cents === null) { toast('That premium is not a number.', 'crit'); return; }
    save.disabled = true;
    try {
      await post('/v1/quotes', {
        carrierId: o.carrier.id, productId: o.product.id,
        applicantName: o.applicantName || undefined,
        age: o.applicant.age, sex: o.applicant.sex, tobacco: o.applicant.tobacco,
        state: o.applicant.state, faceCents: o.applicant.faceCents,
        rateClass: rateClass.value.trim() || undefined,
        monthlyCents: cents,
        // Typed by a person from the carrier's own tool. Recorded as such, so
        // anybody reconciling a surprise later knows which kind of figure it is.
        source: 'entered',
        notes: notes.value.trim() || undefined,
      });
      close();
      o.onSaved();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not save that quote.', 'crit');
      save.disabled = false;
    }
  });

  body.append(
    el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:14px',
      text: `${o.product.name}, for a ${o.applicant.age}-year-old in ${o.applicant.state} wanting `
        + `${fmtMoney(o.applicant.faceCents)} of cover. Type in what the carrier's own quoter gave you `
        + 'and it is recorded as entered by you rather than read off a card.',
    }),
    el('div', { class: 'filters' },
      field('Monthly premium ($)', monthly), field('Class', rateClass)),
    field('Notes', notes),
    save,
  );
}

/* ---------------------------------------------------- how much they need -- */

function openNeeds(onUse: (suggestedFaceCents: number) => void): void {
  const body = el('div');
  const close = drawer('How much do they need?', body);

  const income = input({ placeholder: '62000', inputmode: 'decimal' });
  const years = input({ type: 'number', min: '0', max: '60', value: '10' });
  const mortgage = input({ placeholder: '180000', inputmode: 'decimal' });
  const debts = input({ placeholder: '14000', inputmode: 'decimal' });
  const education = input({ placeholder: '60000', inputmode: 'decimal' });
  const final = input({ placeholder: '12000', inputmode: 'decimal' });
  const existing = input({ placeholder: '50000', inputmode: 'decimal' });
  const assets = input({ placeholder: '20000', inputmode: 'decimal' });
  const out = el('div', { style: 'margin-top:14px' });

  const work = button('Work it out', 'pri', async () => {
    work.disabled = true;
    try {
      const res = await post<Needs>('/v1/quote/needs', {
        annualIncomeCents: toCents(income.value) ?? 0,
        incomeYears: Number(years.value) || 0,
        mortgageCents: toCents(mortgage.value) ?? 0,
        debtsCents: toCents(debts.value) ?? 0,
        educationCents: toCents(education.value) ?? 0,
        finalExpensesCents: toCents(final.value) ?? 0,
        existingCoverCents: toCents(existing.value) ?? 0,
        liquidAssetsCents: toCents(assets.value) ?? 0,
      });
      clear(out);
      out.append(
        table(['', 'Amount'], [
          ...res.lines.map((l) => ({ cells: [l.label, mono(fmtMoney(l.cents))] })),
          { cells: [raw('<b>What the family would need</b>'), el('b', { class: 'mono', text: fmtMoney(res.totalNeedCents) })] },
          { cells: ['Cover and savings they already have', mono(`− ${fmtMoney(res.offsetCents)}`)] },
          { cells: [raw('<b>The gap</b>'), el('b', { class: 'mono', text: fmtMoney(res.gapCents) })] },
        ]),
        el('div', { class: 'okbox', style: 'margin-top:12px' },
          el('b', null, `Quote ${fmtMoney(res.suggestedFaceCents)}. `),
          'Rounded up to a face amount carriers actually sell. It is a starting figure for a '
          + 'conversation, worked out from what they told you — not an underwriting decision.'),
        el('div', { style: 'margin-top:12px' },
          button(`Use ${fmtMoney(res.suggestedFaceCents)}`, 'pri', () => {
            onUse(res.suggestedFaceCents);
            close();
          })),
      );
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not work that out.', 'crit');
    } finally {
      work.disabled = false;
    }
  });

  body.append(
    el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:14px',
      text: 'Debt, income, mortgage, education — what the industry teaches and what you can explain '
        + 'across a kitchen table in a minute. Every figure is theirs, so the answer is exactly as '
        + 'good as what they told you.',
    }),
    el('div', { class: 'filters' },
      field('Household income ($ a year)', income),
      field('Replace it for (years)', years),
      field('Mortgage ($)', mortgage)),
    el('div', { class: 'filters' },
      field('Other debts ($)', debts),
      field('Education ($)', education),
      field('Final expenses ($)', final)),
    el('div', { class: 'filters' },
      field('Cover they already have ($)', existing),
      field('Savings they could use ($)', assets)),
    work, out,
  );
}
